import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import {
  ReferenceManyField,
  required,
  SearchInput,
  SelectInput,
  TextField,
  useRecordContext,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';

import {
  GetInstrumentCountriesStatusQuery,
  GetInstrumentCountriesStatusResponseOne,
  getInstrumentCountriesStatusSortColumns,
} from '@Common/types/bo/instruments';
import { SharesStatus } from '@Common/types/instruments/instrument';

import { DatagridTyped, Pagination, TypographyTranslated } from '@sh4res/bo-common/components';
import {
  countryEntityChoices,
  sharesStatusChoices,
} from '@sh4res/bo-common/components/Instruments/countries_status/choices';
import { EditInstrumentFormData } from '@sh4res/bo-common/components/Instruments/types';
import { SimpleForm } from '@sh4res/bo-common/components/SimpleForm';
import { IdField, SelectChipField } from '@sh4res/bo-common/fields';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';

const testId = 'BoExternalInstrumentsCountriesStatusList';

const SelectSharesStatus = () => {
  const record = useRecordContext<GetInstrumentCountriesStatusResponseOne>();

  if (!record) {
    return null;
  }

  return (
    <SelectInput
      source={`countries_status.${record.id}`}
      choices={sharesStatusChoices}
      label="resources.instruments/countries_status.fields.shares_status"
      validate={[required()]}
      sx={{ maxHeight: '50px' }}
      data-testid={`${testId}_SharesStatusInput`}
    />
  );
};

const SetAllOverride = () => {
  const form = useFormContext<EditInstrumentFormData>();

  const handleSetAll = useCallback(
    (sharesStatus: SharesStatus) => {
      const formValues = form.getValues('countries_status');
      for (const key in formValues) {
        form.setValue(`countries_status.${key}`, sharesStatus);
      }
    },
    [form],
  );

  return (
    <Box display="flex" flexDirection="row">
      <TypographyTranslated
        variant="body1"
        label="resources.instruments/countries_status.set_all"
        sx={{ marginTop: '20px', marginRight: '20px' }}
      />
      <SelectInput
        source="set_all" // non-existent field
        choices={sharesStatusChoices}
        label="resources.instruments/countries_status.fields.shares_status"
        onChange={(event) => handleSetAll(event.target.value)}
        data-testid="BoExternalSetAllOverride_SetAllInput"
      />
    </Box>
  );
};

const instrumentCountriesStatusEditColumns: ColumnList<GetInstrumentCountriesStatusResponseOne> = {
  // eslint-disable-next-line @sh4res/jsx-id-field-for-resources -- No list view for countries
  id: <TextField data-testid={`${testId}_EditIdField`} />,
  name: <TextField data-testid={`${testId}_EditNameField`} />,
  entity: (
    <SelectChipField
      source=""
      choices={countryEntityChoices}
      data-testid={`${testId}_EditEntityField`}
    />
  ),
  shares_status: <SelectSharesStatus />,
  instrument_id: (
    <IdField reference="instruments" data-testid={`${testId}_EditInstrumentIdField`} />
  ),
};

const instrumentCountriesStatusListColumns: ColumnList<GetInstrumentCountriesStatusResponseOne> = {
  // eslint-disable-next-line @sh4res/jsx-id-field-for-resources -- No list view for countries
  id: <TextField data-testid={`${testId}_ListIdField`} />,
  name: <TextField data-testid={`${testId}_ListNameField`} />,
  entity: (
    <SelectChipField
      source=""
      choices={countryEntityChoices}
      data-testid={`${testId}_ListEntityField`}
    />
  ),
  shares_status: (
    <SelectChipField
      source=""
      choices={sharesStatusChoices}
      data-testid={`${testId}_ListSharesStatusField`}
    />
  ),
  instrument_id: (
    <IdField reference="instruments" data-testid={`${testId}_ListInstrumentIdField`} />
  ),
};

const defaultSort: RaSort<GetInstrumentCountriesStatusQuery> = { field: 'name', order: 'ASC' };

export const CountriesStatusList = ({ mode }: { mode: 'list' | 'edit' }) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [accordionExpanded, setAccordionExpanded] = useState<boolean>(false);

  const handleAccordionChange = (_: React.SyntheticEvent<Element, Event>, isExpanded: boolean) => {
    setAccordionExpanded(isExpanded);
  };

  const handleSearchInput = (value: string) => {
    setSearchValue(value);
  };

  const debouncedHandleSearchInput = useMemo(() => debounce(handleSearchInput, 500), []);

  return (
    <Accordion sx={{ width: '100%' }} onChange={handleAccordionChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <TypographyTranslated
          variant="body1"
          label="resources.instruments/countries_status.title"
        />
        {accordionExpanded && (
          <SimpleForm
            toolbar={false}
            sx={{ maxHeight: '50px', marginLeft: '20px', padding: '0px' }}>
            <SearchInput
              source="search_country"
              alwaysOn
              onChange={(event) => debouncedHandleSearchInput(event.target?.value || '')}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.key === 'Enter' && event.preventDefault()}
              data-testid={`${testId}_SearchInput`}
            />
          </SimpleForm>
        )}
      </AccordionSummary>
      <AccordionDetails>
        {mode === 'edit' && <SetAllOverride />}
        <ReferenceManyField
          filter={{ query: searchValue }}
          reference="instruments/countries_status"
          source="id"
          target="instrument_id"
          sort={defaultSort}
          pagination={<Pagination />}
          data-testid={`${testId}_CountriesStatusField`}>
          <DatagridTyped<GetInstrumentCountriesStatusResponseOne>
            bulkActionButtons={false}
            empty={<Typography variant="body1">No countries</Typography>}
            sortableColumns={getInstrumentCountriesStatusSortColumns}
            columnRegistry={
              mode === 'edit'
                ? instrumentCountriesStatusEditColumns
                : instrumentCountriesStatusListColumns
            }
            displayedColumns={['name', 'id', 'entity', 'shares_status']}
          />
        </ReferenceManyField>
      </AccordionDetails>
    </Accordion>
  );
};
